// src/ml/poseToRigify.js

/**
 * V1 pose selector for the runtime mannequin rig.
 *
 * Strategy:
 * - Legs stay neutral for now.
 * - "Arms at sides" is solver-assisted:
 *   we return a very small seed arm pose plus hangFactor metadata.
 *   The runtime elbow-straightening / hang logic then refines the arm.
 * - "Hand on hip" is preset-driven and tuned directly for the runtime rig.
 *
 * Supported V1 arm states:
 * - at_side
 * - on_hip
 *
 * Everything else falls back to:
 * - at_side
 */

/* =========================================================
   RUNTIME POSE LIBRARY
========================================================= */

const POSE_LIBRARY = {
  neutral_legs: {},

  // Seed pose for relaxed arms.
  // These are intentionally subtle; runtime hang logic should refine them.
left_arm_at_side: {
  DEF_shoulderL:  { rotation: [0.00, -0.10, -0.08] },
  DEF_upper_armL: { rotation: [0.01, -0.1, -0.22] },
  DEF_forearmL:   { rotation: [-0.10, 0.3, -0.23] },
  DEF_handL:      { rotation: [-0.16, -0.2, -0.04] },
},

right_arm_at_side: {
  DEF_shoulderR:  { rotation: [0.00, 0.10, 0.08] },
  DEF_upper_armR: { rotation: [-0.01, 0.10, 0.22] },
  DEF_forearmR:   { rotation: [-0.10, -0.3, 0.23] },
  DEF_handR:      { rotation: [-0.16, 0.2, 0.04] },
},

  // Runtime-tuned presets
  left_hand_on_hip: {
    DEF_upper_armL: { rotation: [0.18, 0.03, 0.42] },
    DEF_forearmL:   { rotation: [-0.38, -0.20, -1.24] },
    DEF_handL:      { rotation: [0.10, -0.20, 0.10] },
  },

  right_hand_on_hip: {
    DEF_upper_armR: { rotation: [0.18, -0.03, -0.42] },
    DEF_forearmR:   { rotation: [-0.38, 0.18, 1.24] },
    DEF_handR:      { rotation: [0.10, 0.20, -0.10] },
  },
};

/* =========================================================
   LANDMARK ACCESS
========================================================= */

const IDX = {
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
};

function lm(landmarks, index) {
  return landmarks?.[index] || null;
}

function hasPoint(p) {
  return !!p && Number.isFinite(p.x) && Number.isFinite(p.y);
}

function mid(a, b) {
  return {
    x: ((a?.x ?? 0) + (b?.x ?? 0)) / 2,
    y: ((a?.y ?? 0) + (b?.y ?? 0)) / 2,
    z: ((a?.z ?? 0) + (b?.z ?? 0)) / 2,
  };
}

function dist2(a, b) {
  if (!hasPoint(a) || !hasPoint(b)) return Infinity;
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

function angleABC(a, b, c) {
  if (!hasPoint(a) || !hasPoint(b) || !hasPoint(c)) return Math.PI;

  const abx = a.x - b.x;
  const aby = a.y - b.y;
  const cbx = c.x - b.x;
  const cby = c.y - b.y;

  const dot = abx * cbx + aby * cby;
  const magAB = Math.hypot(abx, aby);
  const magCB = Math.hypot(cbx, cby);
  if (magAB < 1e-6 || magCB < 1e-6) return Math.PI;

  const cos = Math.max(-1, Math.min(1, dot / (magAB * magCB)));
  return Math.acos(cos);
}

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

/* =========================================================
   BONE NAME HELPERS
========================================================= */

function runtimeBoneName(key) {
  return String(key).replace(/^DEF_/, "DEF-");
}

function getPreset(name) {
  const src = POSE_LIBRARY[name] || {};
  return Object.entries(src).map(([name, def]) => ({
    name: runtimeBoneName(name),
    rot: Array.isArray(def?.rotation) ? [...def.rotation] : [0, 0, 0],
  }));
}

function mergeBoneLists(...lists) {
  const map = new Map();

  for (const list of lists) {
    for (const b of list || []) {
      map.set(b.name, b);
    }
  }

  return Array.from(map.values());
}

export function buildHandsAtSidesPoseSpec() {
  const legBones = getPreset("neutral_legs");
  const leftArmBones = getPreset("left_arm_at_side");
  const rightArmBones = getPreset("right_arm_at_side");

  const bones = mergeBoneLists(
    legBones,
    leftArmBones,
    rightArmBones
  );

  return {
    bones,
    meta: {
      source: "fixed_showroom_pose",
      base: {
        preset: "neutral_legs",
        side: "L",
      },
      arm: {
        L: {
          state: "at_side",
          hangFactor: 1.0,
        },
        R: {
          state: "at_side",
          hangFactor: 1.0,
        },
      },
    },
  };
}

/* =========================================================
   CLASSIFICATION
========================================================= */

function classifyArmState(landmarks, side = "L") {
  const shoulder = lm(landmarks, side === "L" ? IDX.LEFT_SHOULDER : IDX.RIGHT_SHOULDER);
  const elbow = lm(landmarks, side === "L" ? IDX.LEFT_ELBOW : IDX.RIGHT_ELBOW);
  const wrist = lm(landmarks, side === "L" ? IDX.LEFT_WRIST : IDX.RIGHT_WRIST);
  const hip = lm(landmarks, side === "L" ? IDX.LEFT_HIP : IDX.RIGHT_HIP);

  const otherShoulder = lm(landmarks, side === "L" ? IDX.RIGHT_SHOULDER : IDX.LEFT_SHOULDER);
  const otherHip = lm(landmarks, side === "L" ? IDX.RIGHT_HIP : IDX.LEFT_HIP);

  if (!hasPoint(shoulder) || !hasPoint(elbow) || !hasPoint(wrist) || !hasPoint(hip)) {
    return { state: "at_side", hangFactor: 1.0 };
  }

  const midShoulders = mid(shoulder, otherShoulder || shoulder);
  const midHips = mid(hip, otherHip || hip);

  const shoulderWidth = Math.max(0.05, dist2(shoulder, otherShoulder || shoulder));
  const torsoHeight = Math.max(0.05, Math.abs(midHips.y - midShoulders.y));
  const elbowAngle = angleABC(shoulder, elbow, wrist);

  const wristToHip = dist2(wrist, hip);
  const handNearHip = wristToHip < shoulderWidth * 0.45;
  const bent = elbowAngle < 2.35;

  const hanging =
    wrist.y > elbow.y &&
    wrist.y > shoulder.y &&
    Math.abs(wrist.x - shoulder.x) < shoulderWidth * 0.9 &&
    elbowAngle > 2.2;

  if (handNearHip && bent) {
    return { state: "on_hip", hangFactor: 0.0 };
  }

  if (hanging) {
    return { state: "at_side", hangFactor: 1.0 };
  }

  // Unsupported / ambiguous states fall back to at-side.
  return { state: "at_side", hangFactor: clamp01((elbowAngle - 1.8) / 1.2) };
}

function classifyLegBase() {
  return { preset: "neutral_legs", side: "L" };
}

/* =========================================================
   POSE BUILD
========================================================= */

export function poseSpecFromLandmarks(landmarks = []) {
  const legBase = classifyLegBase(landmarks);

  const leftArm = classifyArmState(landmarks, "L");
  const rightArm = classifyArmState(landmarks, "R");

  const legBones = getPreset(legBase.preset);

  const leftArmBones =
    leftArm.state === "on_hip"
      ? getPreset("left_hand_on_hip")
      : getPreset("left_arm_at_side");

  const rightArmBones =
    rightArm.state === "on_hip"
      ? getPreset("right_hand_on_hip")
      : getPreset("right_arm_at_side");

  const bones = mergeBoneLists(
    legBones,
    leftArmBones,
    rightArmBones
  );

  return {
    bones,
    meta: {
      base: {
        preset: legBase.preset,
        side: legBase.side,
      },
      arm: {
        L: {
          state: leftArm.state,
          hangFactor: leftArm.state === "at_side" ? 1.0 : 0.0,
        },
        R: {
          state: rightArm.state,
          hangFactor: rightArm.state === "at_side" ? 1.0 : 0.0,
        },
      },
    },
  };
}